import React, { useEffect, useRef, useState } from "react";
import { adminApi, authStore, isAbortError } from "../../api/adminApi";
import "../module.scss";
import BehaviourReport from "./BehaviourReport";
import AnalyticsTabs from "./AnalyticsTabs";
import CustomFunnelEditor from "./CustomFunnelEditor";
import { useAnalyticsProvider } from "./AnalyticsProviderSelect";
import "./analytics-overview.scss";

const outcomeLabels = {
  ai_unanswered: "AI unavailable or no clear answer",
  search_no_results: "Search had no results",
  link_opened: "Suggested link opened",
  support_requested: "Support requested",
};
const activityCards = [
  ["lens.page", "Page views", "Tracked page visits"],
  ["lens.click", "Clicks", "Tracked clicks on pages"],
  ["widget_prompt", "Chat prompts", "Messages sent to the chatbot"],
  ["link_opened", "Links opened", "Links opened from the widget"],
];
const funnelOptions = {
  search: { label: "Find information", steps: ["Opened the widget", "Sent a chat prompt", "Opened a widget link"] },
  support: { label: "Request support", steps: ["Opened the widget", "Requested support"] },
  link: { label: "Open a useful link", steps: ["Opened the widget", "Opened a widget link"] },
};
const funnelEventLabels = {
  "lens.page": "Visited a tracked page",
  "lens.click": "Clicked on a tracked page",
  widget_open: "Opened the widget",
  search_performed: "Searched in the widget",
  widget_prompt: "Sent a chat prompt",
  link_opened: "Opened a widget link",
  support_requested: "Requested support",
  ai_answered: "Received an AI answer",
  ai_unanswered: "AI could not answer",
  search_no_results: "Search found no results",
};
const emptyFunnelSteps = () => [{ event: "lens.page" }, { event: "support_requested" }];
const normalizeFunnelStep = step => typeof step === "string" ? { event: step } : { ...step };
const funnelStepLabel = step => {
  const item = normalizeFunnelStep(step);
  return [funnelEventLabels[item.event] || item.event, item.path && `on ${item.path}`, item.label, item.query && `“${item.query}”`, item.destination && `to ${item.destination}`].filter(Boolean).join(" · ");
};
const TermList = ({ rows, emptyMessage, formatNumber }) => {
  const [page, setPage] = useState(1);
  if (!rows?.length) return <p className="analytics-overview__empty">{emptyMessage}</p>;
  const pageSize = 10;
  const pages = Math.ceil(rows.length / pageSize);
  const currentPage = Math.min(page, pages);
  const visible = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  return <>
    <ol className="analytics-overview__terms" start={(currentPage - 1) * pageSize + 1}>{visible.map(row => <li key={row.query}><span title={row.query}>{row.query}</span><strong>{formatNumber(row.total)}</strong></li>)}</ol>
    {pages > 1 && <nav className="analytics-overview__terms-pages" aria-label="Term pages"><span>{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, rows.length)} of {rows.length}</span><button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button><span>Page {currentPage} of {pages}</span><button type="button" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)}>Next</button></nav>}
  </>;
};
const AnalyticsOverview = ({ section = "overview", refreshKey = 0, days = 30, onDaysChange, onRefresh }) => {
  const site = authStore.user().site?.key || "cloudkia";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [funnelType, setFunnelType] = useState("search");
  const [editingFunnel, setEditingFunnel] = useState(false);
  const [funnelDraft, setFunnelDraft] = useState({ name: "", steps: emptyFunnelSteps() });
  const [funnelSaving, setFunnelSaving] = useState(false);
  const [funnelError, setFunnelError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    adminApi
      .analytics(site, days, controller.signal, funnelType)
      .then(setData)
      .catch((e) => { if (!isAbortError(e)) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [site, days, refreshKey, funnelType]);
  if (!data)
    return (
      <div className="page-loading">
        <p>{error || "Loading analytics…"}</p>
      </div>
    );
  const outcomes = Object.entries(outcomeLabels).map(([event_type, label]) => ({ event_type, label, total: Number(data.eventTypes.find(row => row.event_type === event_type)?.total || 0) }));
  const maxEvent = Math.max(1, ...outcomes.map((x) => x.total));
  const maxPage = Math.max(1, ...data.popularPages.map((x) => Number(x.total)));
  const formatNumber = (value) => Number(value || 0).toLocaleString();
  const change = (type) => {
    const current = Number(data.current?.[type]?.events || 0);
    const previous = Number(data.previous?.[type]?.events || 0);
    if (!previous) return current ? "New activity" : "No activity";
    const percent = Math.round(((current - previous) / previous) * 100);
    return `${percent > 0 ? "+" : ""}${percent}% vs previous ${days} days`;
  };
  const searchSessions = Number(data.current?.search_performed?.sessions || 0);
  const promptSessions = Number(data.current?.widget_prompt?.sessions || 0);
  const pageSessions = Number(data.current?.["lens.page"]?.sessions || 0);
  const openedSessions = Number(data.current?.link_opened?.sessions || 0);
  const supportSessions = Number(data.current?.support_requested?.sessions || 0);
  const journey = [
    ["Visited a tracked page", pageSessions],
    ["Sent a chat prompt", promptSessions],
    ["Used a Search step", searchSessions],
    ["Opened a widget link", openedSessions],
    ["Requested support", supportSessions],
  ];
  const funnel = (data.funnelSteps || []).map(funnelStepLabel);
  const funnelCounts = Array.isArray(data.engagementFunnel) ? data.engagementFunnel.map(Number) : [];
  const openFunnelEditor = () => {
    setFunnelDraft(data.customFunnel ? { name: data.customFunnel.name, steps: data.customFunnel.steps.map(normalizeFunnelStep) } : { name: "", steps: emptyFunnelSteps() });
    setFunnelError("");
    setEditingFunnel(true);
  };
  const saveFunnel = async event => {
    event.preventDefault();
    setFunnelError("");
    const steps = funnelDraft.steps.map(step => ({ ...step, path: step.path?.trim() || undefined, destination: step.destination?.trim() || undefined, query: step.query?.trim() || undefined }));
    if (steps.some(step => [step.path, step.destination].some(path => path && (!path.startsWith("/") || path.startsWith("//") || /[?#]/.test(path))))) {
      setFunnelError("Enter a page path such as /faq or /new-page, without a domain, ? or #.");
      return;
    }
    setFunnelSaving(true);
    try {
      const saved = await adminApi.saveAnalyticsFunnel(site, { name: funnelDraft.name.trim(), steps });
      setData(current => ({ ...current, customFunnel: saved }));
      setEditingFunnel(false);
      setFunnelType("custom");
    } catch (exception) {
      setFunnelError(exception.message);
    } finally {
      setFunnelSaving(false);
    }
  };
  return (
    <div className="module-page">
      <div className="module-page__intro analytics-overview__intro">
        <div>
          <h2>{section === "searches" ? "Widget searches" : section === "pages" ? "Pages and outcomes" : "Visitor behaviour"}</h2>
          <p>{section === "searches" ? "Review AI chat prompts and searches made through configured Search flow steps." : section === "pages" ? "See where visitors interacted and what happened after opening the widget." : `Activity recorded by your site tracker and widget in the last ${days} days.`}</p>
        </div>
        <div className="analytics-page-toolbar"><label>Reporting period<select value={days} onChange={event => onDaysChange(event.target.value)}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option></select></label><button type="button" disabled={loading} onClick={onRefresh}>{loading ? "Updating…" : "Refresh data"}</button></div>
      </div>
      {error && <div className="notice notice--error" role="alert">Could not refresh analytics: {error}</div>}
      {loading && <p className="analytics-overview__updating" role="status">Updating the report…</p>}
      {section === "overview" && <div className="analytics-overview__cards">
        {activityCards.map(([type, label, detail]) => <section className="analytics-overview__card" key={type}>
          <span>{label}</span><strong>{formatNumber(data.current?.[type]?.events)}</strong>
          <small>{detail}</small><em>{change(type)}</em>
        </section>)}
      </div>}
      {section === "overview" && <section className="module-card analytics-overview__funnel">
        <header><div><h3>Engagement funnel</h3><p>Unique tracked sessions that completed these actions in order during the last {days} days.</p></div><div className="analytics-overview__funnel-controls"><label className="analytics-overview__funnel-select">Visitor goal<select value={funnelType} onChange={event => setFunnelType(event.target.value)}>{Object.entries(funnelOptions).map(([value, option]) => <option value={value} key={value}>{option.label}</option>)}{data.customFunnel && <option value="custom">{data.customFunnel.name}</option>}</select></label><button type="button" onClick={openFunnelEditor}>{data.customFunnel ? "Edit custom funnel" : "Create custom funnel"}</button></div></header>
        {editingFunnel && <CustomFunnelEditor draft={funnelDraft} onChange={setFunnelDraft} onSave={saveFunnel} onCancel={() => setEditingFunnel(false)} saving={funnelSaving} error={funnelError} catalog={data.funnelCatalog} />}
        {funnelCounts[0] ? <div className="analytics-overview__funnel-steps">{funnel.map((label, index) => {
          const count = funnelCounts[index] || 0;
          const prior = index ? funnelCounts[index - 1] || 0 : count;
          return <div className="analytics-overview__funnel-step" key={label}>
            <div><span>{index + 1}. {label}</span><strong>{formatNumber(count)} sessions</strong><small>{index ? `${prior ? Math.round(count / prior * 100) : 0}% continued from previous step` : "Starting sessions"}</small></div>
            <i><b style={{ width: `${Math.max(0, Math.min(100, count / funnelCounts[0] * 100))}%` }} /></i>
          </div>;
        })}</div> : <p className="analytics-overview__empty">No sessions recorded for the first step in this period.</p>}
        <p className="analytics-overview__funnel-note">{funnelCounts[0] ? `${Math.round((funnelCounts[funnel.length - 1] || 0) / funnelCounts[0] * 100)}% of sessions that completed the first step also completed the final step in order.` : "Goal completion appears when the first step is recorded."} Counts include sessions recorded while site tracking is enabled.</p>
      </section>}
      {section === "overview" && <section className="module-card analytics-overview__detail">
          <header><div><h3>All tracked actions</h3><p>Unique sessions with each action, regardless of order. Counts can differ from the funnel.</p></div></header>
          <div className="bar-list">{journey.map(([label, count]) => <div className="bar-row" key={label}>
            <div><span>{label}</span><strong>{formatNumber(count)}</strong></div>
            <i><b style={{ width: `${pageSessions ? Math.min(100, count / pageSessions * 100) : 0}%` }} /></i>
          </div>)}</div>
      </section>}
      {section === "searches" && <p className="analytics-overview__scope-notice">Normal AI chat messages appear under Chat prompts. The two Search flow sections below only include activity from a configured Search step. A prompt can also trigger a Search step, so the counts may overlap.</p>}
      {section === "searches" && <section className="module-card analytics-overview__detail">
        <header><div><h3>Most common chat prompts</h3><p>Short terms typed into the chatbot. Prompts containing likely contact details or long text are excluded from this list.</p></div></header>
        <TermList rows={data.promptTerms} emptyMessage="No eligible chat prompts recorded in this period." formatNumber={formatNumber} />
      </section>}
      {section === "searches" && <section className="module-card analytics-overview__detail">
          <header><div><h3>Search flow: no results</h3><p>{formatNumber(data.current?.search_no_results?.events)} searches from configured Search steps returned no results. Normal AI chat replies are not included.</p></div></header>
          <TermList rows={data.unansweredSearches} emptyMessage="No recorded terms yet." formatNumber={formatNumber} />
          <small className="analytics-overview__hint">Terms are shown for searches recorded after this update. Older no-result events may have no saved term.</small>
      </section>}
      {section === "searches" && <section className="module-card analytics-overview__searches">
        <header><div><h3>Search flow: terms used</h3><p>Terms passed through configured Search steps, grouped by term. Normal AI chat messages are listed above under Chat prompts.</p></div></header>
        <TermList rows={data.searchTerms} emptyMessage="No searches recorded in this period." formatNumber={formatNumber} />
      </section>}
      {section === "pages" && <div className="analytics-grid">
        <section className="module-card">
          <header>
            <div>
              <h3>Chatbot outcomes</h3>
              <p>Requests that need attention and actions visitors completed</p>
            </div>
          </header>
          <div className="bar-list">
            {(
              outcomes.map((row) => (
                <div className="bar-row" key={row.event_type}>
                  <div>
                    <span>{row.label}</span>
                    <strong>{row.total}</strong>
                  </div>
                  <i>
                    <b
                      style={{
                        width: `${(Number(row.total) / maxEvent) * 100}%`,
                      }}
                    />
                  </i>
                </div>
              ))
            )}
          </div>
        </section>
        <section className="module-card">
          <header>
            <div>
              <h3>Popular pages</h3>
              <p>Pages where visitors used the widget</p>
            </div>
          </header>
          <div className="bar-list">
            {data.popularPages.length ? (
              data.popularPages.map((row) => (
                <div className="bar-row" key={row.page_path}>
                  <div>
                    <span title={row.page_path}>{row.page_path}</span>
                    <strong>{row.total}</strong>
                  </div>
                  <i>
                    <b
                      style={{
                        width: `${(Number(row.total) / maxPage) * 100}%`,
                      }}
                    />
                  </i>
                </div>
              ))
            ) : (
              <div className="page-empty">
                <p>No page activity yet.</p>
              </div>
            )}
          </div>
        </section>
      </div>}
      {section === "pages" && <section className="insight-note">
        <span>i</span>
        <div>
          <h3>How to use these insights</h3>
          <p>
            Frequent searches help us improve menus and default replies. Pages
            with high chatbot usage may need clearer content or more prominent
            dealer information.
          </p>
        </div>
      </section>}
    </div>
  );
};
function VisitorActivity({ days, onDaysChange }) {
  const site = authStore.user().site?.key || "cloudkia";
  const aiProvider = useAnalyticsProvider(site);
  const [report, setReport] = useState(null);
  const [pages, setPages] = useState([]);
  const [page, setPage] = useState("");
  const [device, setDevice] = useState("desktop");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [engagementAnalysis, setEngagementAnalysis] = useState(null);
  const [engagementError, setEngagementError] = useState("");
  const [engagementLoading, setEngagementLoading] = useState(false);
  const selectionRef = useRef("");
  const analysisController = useRef(null);
  useEffect(() => () => analysisController.current?.abort(), []);
  useEffect(() => { analysisController.current?.abort(); setEngagementAnalysis(null); setEngagementError(""); }, [aiProvider.selection.key, aiProvider.selection.model]);

  const resetActivity = async () => {
    if (!window.confirm("Delete all recorded visitor activity for this site? Heatmap spots, clicks, and interaction totals will start again from zero. This cannot be undone.")) return;
    setResetting(true);
    setResetMessage("");
    try {
      await adminApi.resetVisitorActivity(site);
      setPage("");
      setPages([]);
      setReport(null);
      setLastUpdated(null);
      setRefresh(value => value + 1);
      setResetMessage("Visitor activity cleared. New activity will appear as visitors use the site.");
    } catch (exception) {
      setResetMessage(`Could not reset visitor activity: ${exception.message}`);
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    if (!trackingEnabled) return undefined;
    const timer = window.setInterval(() => {
      if (!resetting && document.visibilityState === "visible") setRefresh(value => value + 1);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [trackingEnabled, resetting]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    adminApi
      .settings(site, controller.signal)
      .then((result) => {
        if (!active) return;
        setTrackingEnabled(result?.settings?.trackVisitors ?? true);
        setWebsiteUrl(result?.settings?.websiteUrl || "");
      })
      .catch(() => {
        if (active) setTrackingEnabled(true);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [site]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const selection = JSON.stringify([site, page, device, days]);
    if (selectionRef.current !== selection) {
      selectionRef.current = selection;
      setLoading(true);
      setReport(null);
      setLastUpdated(null);
      setEngagementAnalysis(null);
      setEngagementError("");
    }
    setError("");
    if (!trackingEnabled) {
      selectionRef.current = "";
      setReport(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    adminApi
      .behaviour(site, { page, device, days }, controller.signal)
      .then((result) => {
        if (!active) return;
        setReport(result);
        setPages(result.pages || []);
        setLastUpdated(new Date());
      })
      .catch((exception) => {
        if (active && !isAbortError(exception)) setError(exception.message);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [site, page, device, days, refresh, trackingEnabled]);

  const pageOptions = Array.from(new Set([...pages, ...(page ? [page] : [])]));
  const analyzeEngagement = async () => {
    if (!report?.page) return;
    analysisController.current?.abort();
    const controller = new AbortController();
    analysisController.current = controller;
    setEngagementLoading(true);
    setEngagementError("");
    setEngagementAnalysis(null);
    try {
      const result = await adminApi.analyzeEngagement(site, { page: report.page, device, days: Number(days), providerKey: aiProvider.selection.key, model: aiProvider.selection.model }, controller.signal);
      if (!controller.signal.aborted) setEngagementAnalysis(result);
    } catch (exception) { if (!isAbortError(exception)) setEngagementError(exception.message); }
    finally { if (analysisController.current === controller) { analysisController.current = null; setEngagementLoading(false); } }
  };
  return (
    <BehaviourReport
      report={report}
      loading={loading}
      error={error}
      liveDevice={device}
      onDeviceChange={setDevice}
      trackingEnabled={trackingEnabled}
      websiteUrl={websiteUrl}
      aiAnalysis={engagementAnalysis}
      aiError={engagementError}
      aiLoading={engagementLoading}
      aiProvider={aiProvider}
      onAnalyze={analyzeEngagement}
      controls={
        <div className="report-filters">
          <label>
            Tracked page
            <select
              value={page || report?.page || ""}
              onChange={(event) => setPage(event.target.value)}
              disabled={loading || !pageOptions.length}
            >
              <option value="" disabled>
                Select a page
              </option>
              {pageOptions.map((path) => (
                <option key={path} value={path}>
                  {path}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reporting period
            <select value={days} onChange={event => onDaysChange(event.target.value)}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </label>
          <button
            type="button"
            disabled={!trackingEnabled || loading || refreshing}
            onClick={() => {
              setRefreshing(true);
              setRefresh(value => value + 1);
            }}
          >
            {loading ? "Loading…" : refreshing ? "Refreshing…" : "Refresh report"}
          </button>
          {/* <button className="report-filters__reset" type="button" disabled={loading || refreshing || resetting} onClick={resetActivity}>
            {resetting ? "Resetting…" : "Reset visitor activity"}
          </button> */}
          {lastUpdated && <small className="report-filters__updated" role="status">Updated {lastUpdated.toLocaleTimeString("en-SG")} · {report?.metrics.clicks ?? 0} clicks on this page</small>}
          {resetMessage && <small className="report-filters__message" role="status">{resetMessage}</small>}
        </div>
      }
    >
    </BehaviourReport>
  );
}

const analyticsTabs = [
  { key: "overview", label: "Overview", description: "Counts and engagement funnel" },
  { key: "searches", label: "Prompts & searches", description: "Chat prompts and Search flow terms" },
  { key: "pages", label: "Pages & outcomes", description: "Page activity and chatbot results" },
  { key: "activity", label: "Activity map", description: "Clicks, scrolling, and page notes" },
];

export default function Analytics() {
  const [activeTab, setActiveTab] = useState("overview");
  const [days, setDays] = useState("30");
  const [refreshKey, setRefreshKey] = useState(0);
  return <div className="module-page">
    <AnalyticsTabs id="analytics" tabs={analyticsTabs} active={activeTab} onChange={setActiveTab} />
    <p className="analytics-overview__scope-notice">Reports include page visits, clicks, widget/chatbot interactions, and available browser/network details from sessions recorded while site tracking is enabled. Visitor on/off choices are not used.</p>
    <div id={`analytics-panel-${activeTab}`} role="tabpanel" aria-labelledby={`analytics-tab-${activeTab}`} tabIndex={0}>
      {activeTab === "activity" ? <VisitorActivity days={days} onDaysChange={setDays} /> : <AnalyticsOverview section={activeTab} days={Number(days)} onDaysChange={setDays} onRefresh={() => setRefreshKey(value => value + 1)} refreshKey={refreshKey} />}
    </div>
  </div>;
}
